/**
 * uuidv7.ts
 *
 * Zero-dependency RFC 9562-compliant UUIDv7 generator.
 * Top 48 bits: big-endian Unix timestamp in milliseconds.
 * Bits 48-51: version (7).
 * Bits 64-65: RFC 4122 variant (0b10).
 * Remaining 74 bits: cryptographically random.
 *
 * Suitable for all mesh entity IDs: commandId, contactId, dealId,
 * proposalId, snapshotId, eventId — all naturally time-ordered in
 * VaultDB / IndexedDB B-tree indexes without a secondary createdAt lookup.
 */

/**
 * Generates an RFC 9562 UUIDv7 string.
 * @param nowMs Optional timestamp override (defaults to Date.now()).
 *              Use a fixed value in tests to get deterministic output.
 */
export function generateUUIDv7(nowMs: number = Date.now()): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  // ── Embed 48-bit millisecond timestamp in bytes 0-5 ──────────────────────
  bytes[0] = (nowMs / 0x10000000000) & 0xff;
  bytes[1] = (nowMs / 0x100000000) & 0xff;
  bytes[2] = (nowMs / 0x1000000) & 0xff;
  bytes[3] = (nowMs / 0x10000) & 0xff;
  bytes[4] = (nowMs / 0x100) & 0xff;
  bytes[5] = nowMs & 0xff;

  // ── Version 7 (0b0111) in high nibble of byte 6 ──────────────────────────
  bytes[6] = (bytes[6] & 0x0f) | 0x70;

  // ── RFC 4122 variant (0b10xx) in high bits of byte 8 ────────────────────
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  // ── Format: 8-4-4-4-12 ──────────────────────────────────────────────────
  const h = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/**
 * Extracts the millisecond timestamp embedded in a UUIDv7 string.
 * Returns NaN for non-v7 UUIDs.
 */
export function getUUIDv7TimestampMs(uuid: string): number {
  const hex = uuid.replace(/-/g, '');
  if (hex.length !== 32) return NaN;
  return parseInt(hex.slice(0, 12), 16);
}

/**
 * Returns true if the string is a plausible UUIDv7 (version nibble === 7).
 */
export function isUUIDv7(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}
